import React, { useState, useEffect } from 'react';
import { setDealerAssistant, getAllDealerAssistant, deleteDealerAssistant, deleteInventory, deleteScrapeJob, getCmsData, getModelsData, getCrmData, createScrapeJob, startScrapeJob, convertLeadsToDealerLeads, getInstructionIds, getToolsData, getMobileImagesData, getImageDimensionsData, getTalkliveInstances } from '../services/api';
import Select from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import ImageDropdown from './ImageDropdown';
import IconDropdown from './IconDropdown';
import ColorDropdown from './ColorDropdown';
import SearchableDropdown from './SearchableDropdown';
import VfProjectsDropdown from './VfProjectsDropdown';
import AutoOpenPagesPresetModal from './AutoOpenPagesPresetModal';
import TalkliveSetupModal from './TalkliveSetupModal';
import './AgentEditor.css';

const optional_webchat_settings_fields = ['chat_persistence', 'embed_element', 'color_theme', 'image_src', 'image_settings', 'icons', 'image_src_mobile', 'image_dimensions_mobile']

// PersistenceEnum: session = sessionStorage, local = localStorage, memory = memory
const CHAT_PERSISTENCE_OPTIONS = [
  { key: 'Session', value: 'sessionStorage' },
  { key: 'Local', value: 'localStorage' },
  { key: 'Memory', value: 'memory' },
]

const DEFAULT_TOOL_RESPONSES = {
  tool_response: 'Merci {lead_name}! Nous vous contacterons dans les plus brefs délais.',
  tool_response_afterhour:
    'Merci {lead_name}! Nos conseillers ne sont pas disponibles en ce moment et communiqueront avec vous à l\'ouverture.',
  invalid_phone_fr:
    'Le numéro de téléphone n\'est pas valide. Veuillez vérifier le format et réessayer.',
  invalid_phone_en:
    'The phone number is not valid. Please check the format and try again.',
}

const isEffectivelyEmpty = (value) => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return false;
  if (typeof value === 'boolean') return value === false;
  if (Array.isArray(value)) {
    const filtered = value
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter((item) => item != null && item !== '');
    return filtered.length === 0;
  }
  if (typeof value === 'object') {
    return Object.values(value).every(isEffectivelyEmpty);
  }
  return false;
};

const normalizeOptionalFieldValue = (key, value) => {
  if (key === 'talklive_activix_config') {
    if (!value?.enabled) return null;
    const advisorEmail = value.advisor_email?.trim?.() || '';
    return {
      enabled: true,
      advisor_email: advisorEmail || null,
    };
  }

  if (Array.isArray(value)) {
    const filtered = value
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter((item) => item != null && item !== '');
    return filtered.length > 0 ? filtered : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }

  if (value != null && typeof value === 'object') {
    if (key === 'twilio_numbers') {
      const entries = Object.entries(value).filter(([, v]) => !isEffectivelyEmpty(v));
      return entries.length > 0 ? Object.fromEntries(entries) : null;
    }
    if (key === 'tool_responses') {
      const toolResponse = value.tool_response?.trim?.() || '';
      const toolResponseAfterhour = value.tool_response_afterhour?.trim?.() || '';
      const invalidPhoneFr = value.invalid_phone_fr?.trim?.() || '';
      const invalidPhoneEn = value.invalid_phone_en?.trim?.() || '';
      if (!toolResponse && !toolResponseAfterhour && !invalidPhoneFr && !invalidPhoneEn) {
        return null;
      }
      return {
        tool_response: toolResponse || null,
        tool_response_afterhour: toolResponseAfterhour || null,
        invalid_phone_fr: invalidPhoneFr || null,
        invalid_phone_en: invalidPhoneEn || null,
      };
    }
    return isEffectivelyEmpty(value) ? null : value;
  }

  return value ?? null;
};

const setOptionalFieldOnPayload = (payload, key, value) => {
  if (optional_webchat_settings_fields.includes(key)) {
    payload.webchat_settings[key] = value;
  } else {
    payload[key] = value;
  }
};

const getOptionalFieldFromAssistant = (assistant, key) => {
  if (!assistant) return undefined;
  if (optional_webchat_settings_fields.includes(key)) {
    return assistant.webchat_settings?.[key];
  }
  return assistant[key];
};

const optionalFieldHasStoredValue = (key, value) => {
  if (key === 'talklive_activix_config') return value?.enabled === true;
  return value !== null && value !== undefined && value !== '';
};

const getOptionalFieldsToClear = ({
  includeFields,
  optionalFields,
  loadedIncludeFields,
  assistantSnapshot,
}) => {
  const toClear = [];

  Object.keys(includeFields).forEach((key) => {
    const storedValue = getOptionalFieldFromAssistant(assistantSnapshot, key);
    const hadStoredValue = optionalFieldHasStoredValue(key, storedValue);
    const wasIncluded = loadedIncludeFields?.[key] || hadStoredValue;

    if (!wasIncluded) return;

    if (!includeFields[key]) {
      toClear.push(key);
      return;
    }

    if (normalizeOptionalFieldValue(key, optionalFields[key]) === null) {
      toClear.push(key);
    }
  });

  return toClear;
};

const buildOptionalFieldsFromAssistant = (assistant, optionalFieldKeys) => {
  const defaultOptionalFields = {
    emails: [],
    group: '',
    activix_api_secret_id: '',
    talklive_activix_config: { enabled: false, advisor_email: '' },
    adf_emails: [],
    d2c_id: '',
    ga4_id: '',
    twilio_numbers: { sms: '', confirmation: '', phenix: '', hook: '' },
    messenger_ids: [],
    vf_id: '',
    crm: '',
    service_emails: [],
    confirmation_sms: '',
    activix_hook_sources: [],
    auto_open_time: 3000,
    auto_open_pages: [],
    chat_persistence: 'localStorage',
    embed_element: '',
    color_theme: '',
    image_src: { en: '', fr: '' },
    image_settings: { height: '', width: '' },
    icons: { header: '', avatar: '', banner: '' },
    image_src_mobile: '',
    image_dimensions_mobile: { width: '', height: '', left: null, top: null },
    facebook_instructions_id: '',
    sms_instructions_id: '',
    confirmation_instructions_id: '',
    sms_prefill_message: { fr: '', en: '' },
    tool_responses: { tool_response: '', tool_response_afterhour: '' },
    modal_settings: {
      enabled: false,
      trigger_delay_ms: 1500,
      subtitle: '',
      content: {
        fr: {
          title: '',
          message: '',
          btn_primary: 'Démarrer le chat',
          btn_secondary: 'Parler aux ventes',
          btn_secondary_chat_message: 'Je souhaite parler à un conseiller des ventes',
          btn_sms: '📱 Envoyer un SMS'
        },
        en: {
          title: '',
          message: '',
          btn_primary: 'Start Chat',
          btn_secondary: 'Chat with Sales',
          btn_secondary_chat_message: 'I would like to speak with a sales advisor',
          btn_sms: '📱 Text Us'
        }
      }
    },
  };
  const defaultIncludeFields = Object.fromEntries(
    optionalFieldKeys.map((field) => [field, false])
  );

  const optionalFields = { ...defaultOptionalFields };
  const includeFields = { ...defaultIncludeFields };

  optionalFieldKeys.forEach((field) => {
    const fieldValue = getOptionalFieldFromAssistant(assistant, field);
    if (!optionalFieldHasStoredValue(field, fieldValue)) return;

    if (field === 'twilio_numbers' && Array.isArray(fieldValue)) {
      optionalFields[field] = { sms: '', confirmation: '', phenix: '', hook: '' };
    } else if (field === 'talklive_activix_config') {
      optionalFields[field] = {
        enabled: fieldValue?.enabled === true,
        advisor_email: fieldValue?.advisor_email || '',
      };
    } else {
      optionalFields[field] = fieldValue;
    }
    includeFields[field] = true;
  });

  return { optionalFields, includeFields };
};

// Filter instruction IDs to only show latest version of each set
const getLatestVersions = (instructionIds) => {
  const versionMap = new Map();
  
  instructionIds.forEach(id => {
    // Match pattern: {base_name}_v{version_number}
    const match = id.match(/^(.+)_v(\d+)$/);
    if (match) {
      const baseName = match[1];
      const version = parseInt(match[2], 10);
      const current = versionMap.get(baseName);
      
      if (!current || version > current.version) {
        versionMap.set(baseName, { id, version });
      }
    } else {
      // If it doesn't match the version pattern, include it as-is
      versionMap.set(id, { id, version: 0 });
    }
  });
  
  return Array.from(versionMap.values()).map(item => item.id);
};

const DealerFactory = () => {
  const [formData, setFormData] = useState({
    assistant_id: '',
    name: '',
    dealer_url: '',
    cms_type: 'd2c_media', // Default value
    status: 'active', // Default value
    model: 'gpt-4', // Default value
    product: 'capture', // Default value
    instructions_id: '',
    inventory_id: '',
    dealer_doc: {
      default_doc: '',
      additional_docs: []
    },
    brand_doc_id: '',
    tools: [],
    examples: [],
    timestamps: {
      created_at: new Date().toISOString(),
      last_modified: new Date().toISOString()
    },
    webchat_settings: {
      height: '90%',
      mobile_popup: false,
      popup_once: false,
      custom_css: false,
      chatbot_names: { english: '', french: '' },
      slogans: { english: '', french: '' },
      introductions: { english: '', french: '' },
      popups: { english: '', french: '' }
    },
    optionalFields: {
      emails: [],
      group: '',
      activix_api_secret_id: '',
      talklive_activix_config: { enabled: false, advisor_email: '' },
      adf_emails: [],
      d2c_id: '',
      ga4_id: '',
      twilio_numbers: { sms: '', confirmation: '', phenix: '', hook: '' },
      messenger_ids: [],
      vf_id: '',
      crm: '',
      service_emails: [],
      confirmation_sms: '',
      activix_hook_sources: [],
      auto_open_time: 3000,
      auto_open_pages: [],
      chat_persistence: 'localStorage',
      embed_element: '',
      color_theme: '',
      image_src: { en: '', fr: '' },
      image_settings: { height: '', width: '' },
      icons: { header: '', avatar: '', banner: '' },
      image_src_mobile: '',
      image_dimensions_mobile: { width: '', height: '', left: null, top: null },
      facebook_instructions_id: '',
      sms_instructions_id: '',
      confirmation_instructions_id: '',
      sms_prefill_message: { fr: '', en: '' },
      tool_responses: { tool_response: '', tool_response_afterhour: '' },
      modal_settings: {
        enabled: false,
        trigger_delay_ms: 1500,
        subtitle: '',
        content: {
          fr: {
            title: '',
            message: '',
            btn_primary: 'Démarrer le chat',
            btn_secondary: 'Parler aux ventes',
            btn_secondary_chat_message: 'Je souhaite parler à un conseiller des ventes',
            btn_sms: '📱 Envoyer un SMS'
          },
          en: {
            title: '',
            message: '',
            btn_primary: 'Start Chat',
            btn_secondary: 'Chat with Sales',
            btn_secondary_chat_message: 'I would like to speak with a sales advisor',
            btn_sms: '📱 Text Us'
          }
        }
      },
    },
    includeFields: {
      emails: false,
      group: false,
      activix_api_secret_id: false,
      talklive_activix_config: false,
      adf_emails: false,
      d2c_id: false,
      ga4_id: false,
      twilio_numbers: false,
      messenger_ids: false,
      vf_id: false,
      crm: false,
      service_emails: false,
      confirmation_sms: false,
      activix_hook_sources: false,
      auto_open_time: false,
      auto_open_pages: false,
      chat_persistence: false,
      embed_element: false,
      color_theme: false,
      image_src: false,
      image_settings: false,
      icons: false,
      image_src_mobile: false,
      image_dimensions_mobile: false,
      facebook_instructions_id: false,
      sms_instructions_id: false,
      confirmation_instructions_id: false,
      sms_prefill_message: false,
      tool_responses: false,
      modal_settings: false,
    },
    manualAssistantId: {
      enabled: false,
      value: ''
    }
  });
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [cmsOptions, setCmsOptions] = useState([]);
  const [crmOptions, setCrmOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [instructionIdOptions, setInstructionIdOptions] = useState([]);
  const [toolOptions, setToolOptions] = useState([]);
  const [mobileImagesOptions, setMobileImagesOptions] = useState([]);
  const [mobileDimensionsOptions, setMobileDimensionsOptions] = useState([]);
  const [mobileDimensionsMap, setMobileDimensionsMap] = useState({});
  const [modal, setModal] = useState({ open: false, message: '', isError: false });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [talkliveModalOpen, setTalkliveModalOpen] = useState(false);
  const [talkliveInstances, setTalkliveInstances] = useState([]);
  const [loadedIncludeFields, setLoadedIncludeFields] = useState(null);

  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        const response = await getAllDealerAssistant();
        const sortedAssistants = [...response.data.data].sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
        setAssistants(sortedAssistants);
      } catch (error) {
        console.error('Error fetching assistants:', error);
      }
    };
    fetchAssistants();

    // Fetch CMS, model, instruction ID, tools, mobile images, and mobile dimensions options
    const fetchOptions = async () => {
      try {
        const [cmsRes, modelRes, crmRes, instructionRes, toolsRes, mobileImagesRes, mobileDimensionsRes] = await Promise.all([
          getCmsData(),
          getModelsData(),
          getCrmData(),
          getInstructionIds(),
          getToolsData(),
          getMobileImagesData(),
          getImageDimensionsData()
        ]);
        setCmsOptions(cmsRes.data || []);
        setCrmOptions(crmRes.data || []);
        setModelOptions(modelRes.data || []);
        
        // Filter to only show latest version of each instruction set
        const allInstructionIds = instructionRes.data?.document_ids || [];
        const latestVersions = getLatestVersions(allInstructionIds);
        setInstructionIdOptions(latestVersions);
        console.log('Tools API response:', toolsRes.data);
        // Transform object to array format expected by Select component
        const toolsArray = toolsRes.data ? Object.entries(toolsRes.data).map(([key, value]) => ({
          value: value,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        })) : [];
        const smsTools = [
          { value: 'capture_sms_activix', label: 'Capture Sms Activix' },
          { value: 'capture_sms_adf', label: 'Capture Sms Adf' },
          { value: 'capture_sms_adf_dynamic', label: 'Capture Sms Adf Dynamic' },
          { value: 'capture_sms_email', label: 'Capture Sms Email' },
        ];
        const seen = new Set(toolsArray.map((o) => o.value));
        setToolOptions([
          ...toolsArray,
          ...smsTools.filter((o) => !seen.has(o.value)),
        ]);

        // Mobile images: object of name -> string (url) or array of strings
        const mobileImagesData = mobileImagesRes.data || {};
        const mobileImagesArr = Array.isArray(mobileImagesData)
          ? mobileImagesData.map((v, i) => ({ key: String(i), value: v }))
          : Object.entries(mobileImagesData).map(([key, value]) => ({ key, value }));
        setMobileImagesOptions(mobileImagesArr);

        // Mobile dimensions: map of named objects e.g. { square: {}, rectangle: {} }
        const mobileDimensionsData = mobileDimensionsRes.data || {};
        setMobileDimensionsMap(mobileDimensionsData);
        setMobileDimensionsOptions(Object.keys(mobileDimensionsData));
      } catch (error) {
        console.error('Error fetching options:', error);
        setToolOptions([]);
        setMobileImagesOptions([]);
        setMobileDimensionsOptions([]);
        setMobileDimensionsMap({});
      }
    };
    fetchOptions();
  }, []);

  const handleAssistantSelect = (assistant) => {
    setSelectedAssistant(assistant);
    const optionalFieldKeys = Object.keys(formData.includeFields);
    const { optionalFields: updatedOptionalFields, includeFields: updatedIncludeFields } =
      buildOptionalFieldsFromAssistant(assistant, optionalFieldKeys);

    setLoadedIncludeFields({ ...updatedIncludeFields });
    setFormData((prevFormData) => ({
      ...prevFormData,
      ...assistant,
      optionalFields: updatedOptionalFields,
      includeFields: updatedIncludeFields,
    }));

    setTalkliveInstances(assistant.talklive_instances || []);
  };

  const handleToggleCreateEdit = () => {
    setSelectedAssistant(null);
    setTalkliveInstances([]);
    setLoadedIncludeFields(null);
    setFormData({
      assistant_id: '',
      name: '',
      dealer_url: '',
      cms_type: 'd2c_media', // Default value
      status: 'active', // Default value
      model: 'gpt-4', // Default value
      product: 'capture', // Default value
      instructions_id: '',
      inventory_id: '',
      dealer_doc: {
        default_doc: '',
        additional_docs: []
      },
      brand_doc_id: '',
      tools: [],
      examples: [],
      timestamps: {
        created_at: new Date().toISOString(),
        last_modified: new Date().toISOString()
      },
      webchat_settings: {
        height: '90%',
        mobile_popup: false,
        popup_once: false,
        custom_css: false,
        chatbot_names: { english: '', french: '' },
        slogans: { english: '', french: '' },
        introductions: { english: '', french: '' },
        popups: { english: '', french: '' }
      },
      optionalFields: {
        emails: [],
        group: '',
        activix_api_secret_id: '',
        talklive_activix_config: { enabled: false, advisor_email: '' },
        adf_emails: [],
        d2c_id: '',
        ga4_id: '',
        twilio_numbers: { sms: '', confirmation: '', phenix: '', hook: '' },
        messenger_ids: [],
        vf_id: '',
        crm: '',
        service_emails: [],
        confirmation_sms: '',
        activix_hook_sources: [],
        auto_open_time: 3000,
        auto_open_pages: [],
        chat_persistence: 'localStorage',
        embed_element: '',
        color_theme: '',
        image_src: { en: '', fr: '' },
        image_settings: { height: '', width: '' },
        icons: { header: '', avatar: '', banner: '' },
        image_src_mobile: '',
        image_dimensions_mobile: { width: '', height: '', left: null, top: null },
        facebook_instructions_id: '',
        sms_instructions_id: '',
        confirmation_instructions_id: '',
        sms_prefill_message: { fr: '', en: '' },
        tool_responses: { tool_response: '', tool_response_afterhour: '' },
        modal_settings: {
          enabled: false,
          trigger_delay_ms: 1500,
          subtitle: '',
          content: {
            fr: {
              title: '',
              message: '',
              btn_primary: 'Démarrer le chat',
              btn_secondary: 'Parler aux ventes',
              btn_secondary_chat_message: 'Je souhaite parler à un conseiller des ventes',
              btn_sms: '📱 Envoyer un SMS'
            },
            en: {
              title: '',
              message: '',
              btn_primary: 'Start Chat',
              btn_secondary: 'Chat with Sales',
              btn_secondary_chat_message: 'I would like to speak with a sales advisor',
              btn_sms: '📱 Text Us'
            }
          }
        },
      },
      includeFields: {
        emails: false,
        group: false,
        activix_api_secret_id: false,
        talklive_activix_config: false,
        adf_emails: false,
        d2c_id: false,
        ga4_id: false,
        twilio_numbers: false,
        messenger_ids: false,
        vf_id: false,
        crm: false,
        service_emails: false,
        confirmation_sms: false,
        activix_hook_sources: false,
        auto_open_time: false,
        auto_open_pages: false,
        chat_persistence: false,
        embed_element: false,
        color_theme: false,
        image_src: false,
        image_settings: false,
        icons: false,
        image_src_mobile: false,
        image_dimensions_mobile: false,
        facebook_instructions_id: false,
        sms_instructions_id: false,
        confirmation_instructions_id: false,
        sms_prefill_message: false,
        tool_responses: false,
        modal_settings: false,
      },
      manualAssistantId: {
        enabled: false,
        value: ''
      }
    });
  };



  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const keys = name.split('.');
    setFormData(prev => {
      let updatedData = { ...prev };
      let current = updatedData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = type === 'checkbox' ? checked : value;
      return updatedData;
    });
  };


  const handleSubmit = async (e) => {
    console.log("sending dealer assistant to api ...")
    e.preventDefault();
    
    // Check if manual assistant ID is enabled but empty
    if (formData.manualAssistantId.enabled && !formData.manualAssistantId.value.trim()) {
      setModal({ open: true, message: 'Please enter an Assistant ID when manual mode is enabled.', isError: true });
      return;
    }
    
    try {
      // Clone webchat_settings to ensure immutability
      const webchatSettingsClone = { ...formData.webchat_settings };
      const existingAssistant = assistants.find(
        (assistant) => assistant.assistant_id === formData.assistant_id
      );
      const assistantSnapshot = existingAssistant || selectedAssistant;
      const isUpdate = Boolean(assistantSnapshot);

      const dataToSend = {
        assistant_id: formData.assistant_id,
        name: formData.name,
        dealer_url: formData.dealer_url,
        cms_type: formData.cms_type,
        status: formData.status,
        model: formData.model,
        product: formData.product,
        instructions_id: formData.instructions_id,
        inventory_id: formData.inventory_id,
        dealer_doc: formData.dealer_doc,
        brand_doc_id: formData.brand_doc_id,
        tools: formData.tools,
        examples: formData.examples,
        timestamps: formData.timestamps,
        webchat_settings: webchatSettingsClone
      };

      // Optional checkbox fields: send null only for fields being cleared on update.
      const fieldsToClear = isUpdate
        ? getOptionalFieldsToClear({
            includeFields: formData.includeFields,
            optionalFields: formData.optionalFields,
            loadedIncludeFields,
            assistantSnapshot,
          })
        : [];
      const fieldsToClearSet = new Set(fieldsToClear);

      Object.keys(formData.includeFields).forEach((key) => {
        if (fieldsToClearSet.has(key)) {
          setOptionalFieldOnPayload(dataToSend, key, null);
          return;
        }
        if (!formData.includeFields[key]) return;

        const normalized = normalizeOptionalFieldValue(key, formData.optionalFields[key]);
        if (normalized !== null) {
          setOptionalFieldOnPayload(dataToSend, key, normalized);
        }
      });

      // Re-fetch fresh TalkLive data from the API to avoid overwriting changes
      // made via the TalkLive UI (e.g. include_inventory toggle)
      try {
        const instancesRes = await getTalkliveInstances(dataToSend.assistant_id).catch(() => null);
        const freshInstances = instancesRes?.data?.instances || talkliveInstances;

        if (freshInstances && freshInstances.length > 0) {
          dataToSend.talklive_instances = freshInstances;
          dataToSend.talklive_numbers = freshInstances
            .filter(i => i.twilio_number)
            .map(i => i.twilio_number);
        }
      } catch (err) {
        console.warn("Failed to refresh TalkLive data, using local state:", err);
        if (talkliveInstances && talkliveInstances.length > 0) {
          dataToSend.talklive_instances = talkliveInstances;
          dataToSend.talklive_numbers = talkliveInstances
            .filter(i => i.twilio_number)
            .map(i => i.twilio_number);
        }
      }

      // Check if the assistant already exists (existingAssistant resolved above for optional field clearing)
      let isNewAssistant = false;
      let assistantId = '';
      
      if (existingAssistant) {
        // Use existing assistant_id if the assistant already exists
        assistantId = existingAssistant.assistant_id;
        dataToSend.assistant_id = assistantId;
      } else {
        // Handle manual assistant ID or generate a new one
        if (formData.manualAssistantId.enabled && formData.manualAssistantId.value.trim()) {
          assistantId = formData.manualAssistantId.value.trim();
          dataToSend.assistant_id = assistantId;
          isNewAssistant = true;
        } else {
          // Generate a new assistant_id if it's a new assistant
          const formatNameForId = (name) => {
            return name
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '') // Remove accents but keep base characters
              .replace(/\s+/g, '_') // Replace spaces with underscores
              .replace(/[^a-z0-9_]/g, ''); // Remove special characters except underscores
          };
          
          const formattedName = formatNameForId(dataToSend.name);
          assistantId = dataToSend.group ? `asst_${dataToSend.group}_${formattedName}_${uuidv4().slice(0, 16)}` : `asst_${formattedName}_${uuidv4().slice(0, 16)}`;
          dataToSend.assistant_id = assistantId;
          isNewAssistant = true;
        }
      }
      
      const response = await setDealerAssistant(dataToSend);
      console.log('Dealer assistant set successfully:', response.data);

      const savedAssistant = {
        ...(existingAssistant || {}),
        ...dataToSend,
        webchat_settings: {
          ...(existingAssistant?.webchat_settings || {}),
          ...dataToSend.webchat_settings,
        },
      };
      fieldsToClear.forEach((key) => {
        if (optional_webchat_settings_fields.includes(key)) {
          delete savedAssistant.webchat_settings[key];
        } else {
          delete savedAssistant[key];
        }
      });
      setAssistants((prev) => {
        const index = prev.findIndex((a) => a.assistant_id === savedAssistant.assistant_id);
        if (index >= 0) {
          const next = [...prev];
          next[index] = savedAssistant;
          return next;
        }
        return [...prev, savedAssistant].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
      });
      if (selectedAssistant?.assistant_id === savedAssistant.assistant_id) {
        setSelectedAssistant(savedAssistant);
      }
      
      if (isNewAssistant) {
        // Only trigger scrape job for new assistants
        if (dataToSend.cms_type !== 'other') {
          // Only trigger scrape job for new assistants that are not "other" as cms_type
          try {
            await createScrapeJob({
              assistant_id: assistantId,
              dealer_url: dataToSend.dealer_url,
              cms_type: dataToSend.cms_type,
              status: dataToSend.status
            });
            
            // Wait a moment for the database to process the scrape job creation
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Start the scrape job after creating it
            await startScrapeJob(assistantId);
          } catch (scrapeError) {
            console.error('Error with scrape job:', scrapeError);
              // Continue with the process even if scrape job fails
          }
        }
        // If manual assistant ID was used, trigger the conversion endpoint
        if (formData.manualAssistantId.enabled && formData.manualAssistantId.value.trim()) {
          setIsConverting(true);
          try {
            const conversionResponse = await convertLeadsToDealerLeads(assistantId);
            console.log('Conversion successful:', conversionResponse.data);
            setIsConverting(false);
            setModal({ 
              open: true, 
              message: `Assistant set successfully! ${conversionResponse.data.message}`, 
              isError: false 
            });
          } catch (conversionError) {
            console.error('Error during conversion:', conversionError);
            setIsConverting(false);
            setModal({ 
              open: true, 
              message: 'Assistant set successfully, but conversion failed. Please try again.', 
              isError: true 
            });
          }
        } else {
          setModal({ open: true, message: 'Assistant set successfully!', isError: false });
        }
      } else {
        setModal({ open: true, message: 'Assistant set successfully!', isError: false });
      }
    } catch (error) {
      console.error('Error setting dealer assistant:', error);
      setModal({ open: true, message: 'Error setting assistant. Please try again.', isError: true });
    }
  };



  const handleIncludeFieldChange = (field) => {
    setFormData(prev => {
      const newIncludeValue = !prev.includeFields[field];
      const updatedOptionalFields = { ...prev.optionalFields };
      
      // Initialize twilio_numbers as object when checkbox is checked
      if (field === 'twilio_numbers' && newIncludeValue && (!updatedOptionalFields[field] || Array.isArray(updatedOptionalFields[field]))) {
        updatedOptionalFields[field] = { sms: '', confirmation: '', phenix: '', hook: '' };
      }

      // Talklive Activix: checkbox sets enabled; advisor_email is optional
      if (field === 'talklive_activix_config') {
        updatedOptionalFields[field] = {
          enabled: newIncludeValue,
          advisor_email: newIncludeValue
            ? (updatedOptionalFields[field]?.advisor_email || '')
            : '',
        };
      }

      if (field === 'tool_responses' && newIncludeValue) {
        const current = updatedOptionalFields[field];
        updatedOptionalFields[field] = {
          tool_response: current?.tool_response || DEFAULT_TOOL_RESPONSES.tool_response,
          tool_response_afterhour: current?.tool_response_afterhour || DEFAULT_TOOL_RESPONSES.tool_response_afterhour,
          invalid_phone_fr: current?.invalid_phone_fr || '',
          invalid_phone_en: current?.invalid_phone_en || '',
        };
      }
      
      return {
        ...prev,
        includeFields: {
          ...prev.includeFields,
          [field]: newIncludeValue
        },
        optionalFields: updatedOptionalFields
      };
    });
  };

  const handleOptionalFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      optionalFields: {
        ...prev.optionalFields,
        [field]: value
      }
    }));
  };

  const handlePresetApply = (presetValue) => {
    handleOptionalFieldChange('auto_open_pages', presetValue);
  };

  const handleDeleteAssistant = async () => {
    if (!selectedAssistant) return;

    try {
      console.log("deleting assistant ", selectedAssistant.name, "...");
      // Call the API to delete the assistant
      await deleteDealerAssistant(selectedAssistant.assistant_id);
      await deleteInventory(selectedAssistant.assistant_id);
      await deleteScrapeJob(selectedAssistant.assistant_id);
      console.log('Assistant deleted successfully');

      // Update the state to remove the deleted assistant
      setAssistants(prevAssistants => prevAssistants.filter(assistant => assistant.assistant_id !== selectedAssistant.assistant_id));
      setSelectedAssistant(null);
      setLoadedIncludeFields(null);
      setFormData({
        // Reset formData to default values
        assistant_id: '',
        name: '',
        dealer_url: '',
        cms_type: 'wordpress',
        status: 'active',
        model: 'gpt-4',
        product: 'capture',
        instructions_id: '',
        inventory_id: '',
        dealer_doc: {
          default_doc: '',
          additional_docs: []
        },
        brand_doc_id: '',
        tools: [],
        examples: [],
        timestamps: {
          created_at: new Date().toISOString(),
          last_modified: new Date().toISOString()
        },
        webchat_settings: {
          height: '90%',
          mobile_popup: false,
          popup_once: false,
          custom_css: false,
          chatbot_names: { english: '', french: '' },
          slogans: { english: '', french: '' },
          introductions: { english: '', french: '' },
          popups: { english: '', french: '' }
        },
        optionalFields: {
          emails: [],
          group: '',
          activix_api_secret_id: '',
          talklive_activix_config: { enabled: false, advisor_email: '' },
          adf_emails: [],
          d2c_id: '',
          ga4_id: '',
          twilio_numbers: { sms: '', confirmation: '', phenix: '', hook: '' },
          messenger_ids: [],
          vf_id: '',
          crm: '',
          service_emails: [],
          confirmation_sms: '',
          activix_hook_sources: [],
          auto_open_time: 3000,
          auto_open_pages: [],
          chat_persistence: 'localStorage',
          embed_element: '',
          color_theme: '',
          image_src: { en: '', fr: '' },
          image_settings: { height: '', width: '' },
          icons: { header: '', avatar: '', banner: '' },
          image_src_mobile: '',
          image_dimensions_mobile: { width: '', height: '', left: null, top: null },
          facebook_instructions_id: '',
          sms_instructions_id: '',
          confirmation_instructions_id: '',
          sms_prefill_message: { fr: '', en: '' },
          tool_responses: { tool_response: '', tool_response_afterhour: '' },
          modal_settings: {
            enabled: false,
            trigger_delay_ms: 1500,
            subtitle: '',
            content: {
              fr: {
                title: '',
                message: '',
                btn_primary: 'Démarrer le chat',
                btn_secondary: 'Parler aux ventes',
                btn_secondary_chat_message: 'Je souhaite parler à un conseiller des ventes',
                btn_sms: '📱 Envoyer un SMS'
              },
              en: {
                title: '',
                message: '',
                btn_primary: 'Start Chat',
                btn_secondary: 'Chat with Sales',
                btn_secondary_chat_message: 'I would like to speak with a sales advisor',
                btn_sms: '📱 Text Us'
              }
            }
          },
        },
        includeFields: {
          emails: false,
          group: false,
          activix_api_secret_id: false,
          talklive_activix_config: false,
          adf_emails: false,
          d2c_id: false,
          ga4_id: false,
          twilio_numbers: false,
          messenger_ids: false,
          vf_id: false,
          crm: false,
          service_emails: false,
          confirmation_sms: false,
          activix_hook_sources: false,
          auto_open_time: false,
          auto_open_pages: false,
          chat_persistence: false,
          embed_element: false,
          color_theme: false,
          image_src: false,
          image_settings: false,
          icons: false,
          image_src_mobile: false,
          image_dimensions_mobile: false,
          facebook_instructions_id: false,
          sms_instructions_id: false,
          confirmation_instructions_id: false,
          sms_prefill_message: false,
          tool_responses: false,
          modal_settings: false,
        },
        manualAssistantId: {
          enabled: false,
          value: ''
        }
      });
      setModal({ open: true, message: 'Assistant deleted successfully!', isError: false });
    } catch (error) {
      console.error('Error deleting assistant:', error);
      setModal({ open: true, message: 'Error deleting assistant. Please try again.', isError: true });
    }
    setDeleteModalOpen(false);
  };

  return (
    <div>
      <div className="legacy-banner">
        Legacy: prefer the <strong>Agents</strong> editor for new work. Dealer Factory still dual-writes
        `dealer_assistants` and `agents`.
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <button onClick={handleToggleCreateEdit}>
        {selectedAssistant ? 'Create New Assistant' : 'Edit Existing Assistant'}
      </button>
      {selectedAssistant && (
        <button onClick={() => setDeleteModalOpen(true)}>
          Delete Assistant
        </button>
      )}
      <select onChange={(e) => handleAssistantSelect(assistants[e.target.value])}>
        <option value="">Select an Assistant</option>
        {assistants.map((assistant, index) => (
          <option key={assistant.assistant_id} value={index}>
            {assistant.name}
          </option>
        ))}
      </select>
      <form onSubmit={handleSubmit}>
        {/* Manual Assistant ID Section - Only show when creating new assistant */}
        {!selectedAssistant && (
          <div style={{ 
            border: '1px solid #ddd', 
            padding: '16px', 
            marginBottom: '16px', 
            borderRadius: '4px',
            backgroundColor: '#f9f9f9'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={formData.manualAssistantId.enabled}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  manualAssistantId: {
                    ...prev.manualAssistantId,
                    enabled: e.target.checked
                  }
                }))}
                style={{ marginRight: '8px' }}
              />
              <label>Use Manual Assistant ID</label>
            </div>
            {formData.manualAssistantId.enabled && (
              <input
                type="text"
                value={formData.manualAssistantId.value}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  manualAssistantId: {
                    ...prev.manualAssistantId,
                    value: e.target.value
                  }
                }))}
                placeholder="Enter Assistant ID"
                style={{ width: '100%', padding: '8px', marginTop: '8px' }}
              />
            )}
          </div>
        )}
        
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Assistant Name"
        />
        <input
          type="text"
          name="dealer_url"
          value={formData.dealer_url}
          onChange={handleChange}
          placeholder="Dealer URL"
        />
        <select
          name="cms_type"
          value={formData.cms_type}
          onChange={handleChange}
        >
          <option value="">Select CMS Type</option>
          {Object.entries(cmsOptions).map(([key, value]) => (
            <option key={key} value={value}>{value}</option>
          ))}
        </select>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="demo">Demo</option>
        </select>
        <select
          name="model"
          value={formData.model}
          onChange={handleChange}
        >
          <option value="">Select Model</option>
          {Object.entries(modelOptions)
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([key, value]) => (
              <option key={key} value={value}>{value}</option>
            ))}
        </select>
        <select
          name="product"
          value={formData.product}
          onChange={handleChange}
        >
          <option value="capture">Capture</option>
          <option value="hook">Hook</option>
          <option value="trial">Trial</option>
        </select>
        <select
          name="instructions_id"
          value={formData.instructions_id}
          onChange={handleChange}
        >
          <option value="">Select Instructions ID</option>
          {instructionIdOptions.map((id) => (
            <option key={id} value={id}>{id}</option>
          ))}
        </select>
        <input
          type="text"
          name="inventory_id"
          value={formData.inventory_id}
          onChange={handleChange}
          placeholder="Inventory ID"
        />
        <input
          type="text"
          name="dealer_doc.default_doc"
          value={formData.dealer_doc.default_doc}
          onChange={handleChange}
          placeholder="Dealer Documentation"
        />
        <input
          type="text"
          name="brand_doc_id"
          value={formData.brand_doc_id}
          onChange={handleChange}
          placeholder="Brand Document ID"
        />

        <Select
          isMulti
          name="tools"
          options={toolOptions || []}
          value={(toolOptions || []).filter(option => formData.tools.includes(option.value))}
          onChange={selectedOptions => setFormData(prev => ({
            ...prev,
            tools: selectedOptions.map(option => option.value)
          }))}
          placeholder="Select Tools"
        />
        <input
          type="text"
          name="webchat_settings.height"
          value={formData.webchat_settings.height}
          onChange={handleChange}
          placeholder="Webchat Height"
        />
        <input
          type="checkbox"
          name="webchat_settings.mobile_popup"
          checked={formData.webchat_settings.mobile_popup}
          onChange={handleChange}
          style={{ width: '30px' }}
        /> Mobile Popup
        <input
          type="checkbox"
          name="webchat_settings.popup_once"
          checked={formData.webchat_settings.popup_once}
          onChange={handleChange}
          style={{ width: '30px' }}
        /> Popup Once
        <input
          type="checkbox"
          name="webchat_settings.custom_css"
          checked={formData.webchat_settings.custom_css}
          onChange={handleChange}
          style={{ width: '30px' }}
        /> Custom CSS
        <input
          type="text"
          name="webchat_settings.chatbot_names.english"
          value={formData.webchat_settings.chatbot_names.english}
          onChange={handleChange}
          placeholder="Chatbot Name (English)"
        />
        <input
          type="text"
          name="webchat_settings.chatbot_names.french"
          value={formData.webchat_settings.chatbot_names.french}
          onChange={handleChange}
          placeholder="Chatbot Name (French)"
        />
        <input
          type="text"
          name="webchat_settings.slogans.english"
          value={formData.webchat_settings.slogans.english}
          onChange={handleChange}
          placeholder="Slogan (English)"
        />
        <input
          type="text"
          name="webchat_settings.slogans.french"
          value={formData.webchat_settings.slogans.french}
          onChange={handleChange}
          placeholder="Slogan (French)"
        />
        <input
          type="text"
          name="webchat_settings.introductions.english"
          value={formData.webchat_settings.introductions.english}
          onChange={handleChange}
          placeholder="Introduction (English)"
        />
        <input
          type="text"
          name="webchat_settings.introductions.french"
          value={formData.webchat_settings.introductions.french}
          onChange={handleChange}
          placeholder="Introduction (French)"
        />
        <input
          type="text"
          name="webchat_settings.popups.english"
          value={formData.webchat_settings.popups.english}
          onChange={handleChange}
          placeholder="Popup (English)"
        />
        <input
          type="text"
          name="webchat_settings.popups.french"
          value={formData.webchat_settings.popups.french}
          onChange={handleChange}
          placeholder="Popup (French)"
        />


        {/* Webchat Settings Optional Fields */}
        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.chat_persistence}
            onChange={() => handleIncludeFieldChange('chat_persistence')}
            style={{ width: '30px' }}
          /> Chat Persistence
          {formData.includeFields.chat_persistence && (
            <SearchableDropdown
              options={CHAT_PERSISTENCE_OPTIONS}
              value={formData.optionalFields.chat_persistence || ''}
              onChange={(value) => handleOptionalFieldChange('chat_persistence', value)}
              placeholder="Select chat persistence..."
            />
          )}
        </div>
        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.embed_element}
            onChange={() => handleIncludeFieldChange('embed_element')}
            style={{ width: '30px' }}
          /> Embed Element
          {formData.includeFields.embed_element && (
            <input
              type="text"
              value={formData.optionalFields.embed_element || ''}
              onChange={(e) => handleOptionalFieldChange('embed_element', e.target.value)}
              placeholder="Embed Element"
            />
          )}
        </div>
        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.color_theme}
            onChange={() => handleIncludeFieldChange('color_theme')}
            style={{ width: '30px' }}
          /> Color Theme
          {formData.includeFields.color_theme && (
            <ColorDropdown
              value={formData.optionalFields.color_theme || ''}
              onChange={(value) => handleOptionalFieldChange('color_theme', value)}
              placeholder="Select color theme..."
            />
          )}
        </div>
        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.image_src}
            onChange={() => handleIncludeFieldChange('image_src')}
            style={{ width: '30px' }}
          /> Image Source
          {formData.includeFields.image_src && (
            <ImageDropdown
              value={formData.optionalFields.image_src?.en || ''}
              onChange={(value) => handleOptionalFieldChange('image_src', { ...formData.optionalFields.image_src, en: value })}
              language="english"
              placeholder="Select English image..."
            />
          )}
          {formData.includeFields.image_src && (
            <ImageDropdown
              value={formData.optionalFields.image_src?.fr || ''}
              onChange={(value) => handleOptionalFieldChange('image_src', { ...formData.optionalFields.image_src, fr: value })}
              language="french"
              placeholder="Select French image..."
            />
          )}
        </div>
        {/* <div>
          <input
            type="checkbox"
            checked={formData.includeFields.image_settings}
            onChange={() => handleIncludeFieldChange('image_settings')}
            style={{ width: '30px' }}
          /> Image Settings
          {formData.includeFields.image_settings && (
            <input
              type="text"
              value={formData.optionalFields.image_settings?.height || ''}
              onChange={(e) => handleOptionalFieldChange('image_settings', { ...formData.optionalFields.image_settings, height: e.target.value })}
              placeholder="Image Height"
            />
          )}
          {formData.includeFields.image_settings && (
            <input
              type="text"
              value={formData.optionalFields.image_settings?.width || ''}
              onChange={(e) => handleOptionalFieldChange('image_settings', { ...formData.optionalFields.image_settings, width: e.target.value })}
              placeholder="Image Width"
            />
          )}
        </div> */}
        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.icons}
            onChange={() => handleIncludeFieldChange('icons')}
            style={{ width: '30px' }}
          /> Icons
          {/* {formData.includeFields.icons && (
            <input
              type="text"
              value={formData.optionalFields.icons?.header || ''}
              onChange={(e) => handleOptionalFieldChange('icons', { ...formData.optionalFields.icons, header: e.target.value })}
              placeholder="Header Icon"
            />
          )} */}
          {formData.includeFields.icons && (
            <IconDropdown
              value={formData.optionalFields.icons?.avatar || ''}
              onChange={(value) => handleOptionalFieldChange('icons', { ...formData.optionalFields.icons, avatar: value })}
              placeholder="Select Avatar Icon..."
            />
          )}
          {formData.includeFields.icons && (
            <IconDropdown
              value={formData.optionalFields.icons?.banner || ''}
              onChange={(value) => handleOptionalFieldChange('icons', { ...formData.optionalFields.icons, banner: value })}
              placeholder="Select Banner Icon..."
            />
          )}
        </div>
        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.image_src_mobile}
            onChange={() => handleIncludeFieldChange('image_src_mobile')}
            style={{ width: '30px' }}
          /> Image Source Mobile
          {formData.includeFields.image_src_mobile && (
            <SearchableDropdown
              options={mobileImagesOptions}
              value={formData.optionalFields.image_src_mobile || ''}
              onChange={(value) => handleOptionalFieldChange('image_src_mobile', value)}
              placeholder="Select mobile image..."
            />
          )}
        </div>
        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.image_dimensions_mobile}
            onChange={() => handleIncludeFieldChange('image_dimensions_mobile')}
            style={{ width: '30px' }}
          /> Image Dimensions Mobile
          {formData.includeFields.image_dimensions_mobile && (
            <SearchableDropdown
              options={mobileDimensionsOptions.map((key) => {
                const d = mobileDimensionsMap[key] || {};
                const valueDisplay = [d.height, d.width].some((v) => v != null && v !== '') ? `${d.height ?? '?'} x ${d.width ?? '?'}` : key;
                return {
                  key: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                  value: key,
                  valueDisplay
                };
              })}
              value={
                (() => {
                  const current = formData.optionalFields.image_dimensions_mobile;
                  if (!current || typeof current !== 'object') return '';
                  const match = mobileDimensionsOptions.find(
                    (key) => JSON.stringify(mobileDimensionsMap[key]) === JSON.stringify(current)
                  );
                  return match || '';
                })()
              }
              onChange={(key) => handleOptionalFieldChange('image_dimensions_mobile', key ? mobileDimensionsMap[key] ?? {} : {})}
              placeholder="Select dimensions preset..."
            />
          )}
        </div>

        {/* Modal Settings Section */}
        <div style={{
          border: '2px solid #007bff',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '20px',
          marginBottom: '20px',
          backgroundColor: '#f8f9ff'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#007bff' }}>📱 Modal Settings</h4>
          <div style={{
            backgroundColor: '#e7f3ff',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#0056b3'
          }}>
            ℹ️ Agent Name, Avatar, and Primary Color are automatically inherited from webchat settings above.
          </div>

          <div style={{ marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={formData.includeFields.modal_settings}
              onChange={() => handleIncludeFieldChange('modal_settings')}
              style={{ width: '30px' }}
            /> <strong>Enable Modal Settings</strong>
          </div>

          {formData.includeFields.modal_settings && (
            <div style={{ marginLeft: '20px' }}>
              {/* General Config */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Modal Enabled</label>
                <input
                  type="checkbox"
                  checked={formData.optionalFields.modal_settings?.enabled || false}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    enabled: e.target.checked
                  })}
                  style={{ width: '30px' }}
                /> Active
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Trigger Delay (ms)</label>
                <input
                  type="number"
                  value={formData.optionalFields.modal_settings?.trigger_delay_ms || 1500}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    trigger_delay_ms: parseInt(e.target.value) || 1500
                  })}
                  min="500"
                  max="10000"
                  placeholder="1500"
                  style={{ width: '120px' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Subtitle</label>
                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.subtitle || ''}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    subtitle: e.target.value
                  })}
                  placeholder="401 Dixie Hyundai"
                  style={{ width: '100%' }}
                />
              </div>

              {/* French Content */}
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#fff'
              }}>
                <h5 style={{ marginTop: 0, marginBottom: '12px' }}>🇫🇷 Contenu Français</h5>

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.fr?.title || ''}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      fr: { ...formData.optionalFields.modal_settings?.content?.fr, title: e.target.value }
                    }
                  })}
                  placeholder="Titre FR (ex: Bienvenue chez [Dealer]!)"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <textarea
                  value={formData.optionalFields.modal_settings?.content?.fr?.message || ''}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      fr: { ...formData.optionalFields.modal_settings?.content?.fr, message: e.target.value }
                    }
                  })}
                  placeholder="Message FR"
                  style={{ width: '100%', marginBottom: '8px', minHeight: '60px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.fr?.btn_primary || 'Démarrer le chat'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      fr: { ...formData.optionalFields.modal_settings?.content?.fr, btn_primary: e.target.value }
                    }
                  })}
                  placeholder="Bouton Primaire FR"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.fr?.btn_secondary || 'Parler aux ventes'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      fr: { ...formData.optionalFields.modal_settings?.content?.fr, btn_secondary: e.target.value }
                    }
                  })}
                  placeholder="Bouton Secondaire FR (si pas de SMS)"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.fr?.btn_secondary_chat_message || 'Je souhaite parler à un conseiller des ventes'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      fr: { ...formData.optionalFields.modal_settings?.content?.fr, btn_secondary_chat_message: e.target.value }
                    }
                  })}
                  placeholder="Message auto envoyé au clic btn secondaire"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.fr?.btn_sms || '📱 Envoyer un SMS'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      fr: { ...formData.optionalFields.modal_settings?.content?.fr, btn_sms: e.target.value }
                    }
                  })}
                  placeholder="Bouton SMS FR (si Twilio configuré)"
                  style={{ width: '100%' }}
                />
              </div>

              {/* English Content */}
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '12px',
                backgroundColor: '#fff'
              }}>
                <h5 style={{ marginTop: 0, marginBottom: '12px' }}>🇬🇧 English Content</h5>

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.en?.title || ''}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      en: { ...formData.optionalFields.modal_settings?.content?.en, title: e.target.value }
                    }
                  })}
                  placeholder="Title EN (ex: Welcome to [Dealer]!)"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <textarea
                  value={formData.optionalFields.modal_settings?.content?.en?.message || ''}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      en: { ...formData.optionalFields.modal_settings?.content?.en, message: e.target.value }
                    }
                  })}
                  placeholder="Message EN"
                  style={{ width: '100%', marginBottom: '8px', minHeight: '60px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.en?.btn_primary || 'Start Chat'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      en: { ...formData.optionalFields.modal_settings?.content?.en, btn_primary: e.target.value }
                    }
                  })}
                  placeholder="Primary Button EN"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.en?.btn_secondary || 'Chat with Sales'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      en: { ...formData.optionalFields.modal_settings?.content?.en, btn_secondary: e.target.value }
                    }
                  })}
                  placeholder="Secondary Button EN (if no SMS)"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.en?.btn_secondary_chat_message || 'I would like to speak with a sales advisor'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      en: { ...formData.optionalFields.modal_settings?.content?.en, btn_secondary_chat_message: e.target.value }
                    }
                  })}
                  placeholder="Auto message sent on secondary btn click"
                  style={{ width: '100%', marginBottom: '8px' }}
                />

                <input
                  type="text"
                  value={formData.optionalFields.modal_settings?.content?.en?.btn_sms || '📱 Text Us'}
                  onChange={(e) => handleOptionalFieldChange('modal_settings', {
                    ...formData.optionalFields.modal_settings,
                    content: {
                      ...formData.optionalFields.modal_settings?.content,
                      en: { ...formData.optionalFields.modal_settings?.content?.en, btn_sms: e.target.value }
                    }
                  })}
                  placeholder="SMS Button EN (if Twilio configured)"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Optional Fields Section */}
        {Object.keys(formData.includeFields).map((field) => (
          // Skip rendering for specific fields
          !optional_webchat_settings_fields.includes(field) && field !== 'modal_settings' && field !== 'tool_responses' && (
            <div key={field}>
              <input
                type="checkbox"
                checked={formData.includeFields[field]}
                onChange={() => handleIncludeFieldChange(field)}
                style={{ width: '30px' }}
              /> {field === 'talklive_activix_config' ? 'talklive activix' : field.replace(/_/g, ' ')}
              {formData.includeFields[field] && (
                <div>
                  {field === 'talklive_activix_config' ? (
                    <input
                      type="text"
                      value={formData.optionalFields[field]?.advisor_email || ''}
                      onChange={(e) => handleOptionalFieldChange(field, {
                        enabled: true,
                        advisor_email: e.target.value,
                      })}
                      placeholder="Advisor email (optional)"
                    />
                  ) : field === 'twilio_numbers' ? (
                    <div>
                      <input
                        type="text"
                        value={formData.optionalFields[field]?.sms || ''}
                        onChange={(e) => handleOptionalFieldChange(field, { ...formData.optionalFields[field], sms: e.target.value })}
                        placeholder="SMS"
                      />
                      <input
                        type="text"
                        value={formData.optionalFields[field]?.confirmation || ''}
                        onChange={(e) => handleOptionalFieldChange(field, { ...formData.optionalFields[field], confirmation: e.target.value })}
                        placeholder="Confirmation"
                      />
                      <input
                        type="text"
                        value={formData.optionalFields[field]?.phenix || ''}
                        onChange={(e) => handleOptionalFieldChange(field, { ...formData.optionalFields[field], phenix: e.target.value })}
                        placeholder="Phenix"
                      />
                      <input
                        type="text"
                        value={formData.optionalFields[field]?.hook || ''}
                        onChange={(e) => handleOptionalFieldChange(field, { ...formData.optionalFields[field], hook: e.target.value })}
                        placeholder="Hook"
                      />
                    </div>
                  ) : field === 'sms_prefill_message' ? (
                    <div>
                      <input
                        type="text"
                        value={formData.optionalFields[field]?.fr || ''}
                        onChange={(e) => handleOptionalFieldChange(field, { ...formData.optionalFields[field], fr: e.target.value })}
                        placeholder="FR: Je suis intéressé par un véhicule"
                      />
                      <input
                        type="text"
                        value={formData.optionalFields[field]?.en || ''}
                        onChange={(e) => handleOptionalFieldChange(field, { ...formData.optionalFields[field], en: e.target.value })}
                        placeholder="EN: I am interested in a vehicle"
                      />
                    </div>
                  ) : field === 'vf_id' ? (
                    <VfProjectsDropdown
                      value={formData.optionalFields[field]}
                      onChange={(value) => handleOptionalFieldChange(field, value)}
                      placeholder="Select VF project..."
                    />
                  ) : field === 'crm' ? (
                    <select
                      value={formData.optionalFields[field] || ''}
                      onChange={(e) => handleOptionalFieldChange(field, e.target.value)}
                    >
                      <option value="">Select CRM</option>
                      {Object.entries(crmOptions).map(([key, value]) => (
                        <option key={key} value={value}>{value}</option>
                      ))}
                    </select>
                  ) : field === 'facebook_instructions_id' || field === 'sms_instructions_id' || field === 'confirmation_instructions_id' ? (
                    <select
                      value={formData.optionalFields[field] || ''}
                      onChange={(e) => handleOptionalFieldChange(field, e.target.value)}
                    >
                      <option value="">Select {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                      {instructionIdOptions.map((id) => (
                        <option key={id} value={id}>{id}</option>
                      ))}
                    </select>
                  ) : Array.isArray(formData.optionalFields[field]) ? (
                    <div>
                      {formData.optionalFields[field].map((item, index) => (
                        <div key={`${field}-${index}`}>
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => handleOptionalFieldChange(field, formData.optionalFields[field].map((val, i) => i === index ? e.target.value : val))}
                            placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                          />
                          <button type="button" onClick={() => handleOptionalFieldChange(field, formData.optionalFields[field].filter((_, i) => i !== index))}>Delete</button>
                        </div>
                      ))}
                      {field === 'auto_open_pages' && (
                        <button 
                          type="button" 
                          onClick={() => setPresetModalOpen(true)}
                          style={{ 
                            marginTop: '8px', 
                            padding: '8px 12px', 
                            backgroundColor: '#007bff', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px', 
                            cursor: 'pointer' 
                          }}
                        >
                          Select Preset
                        </button>
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.optionalFields[field]}
                      onChange={(e) => handleOptionalFieldChange(field, e.target.value)}
                      placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                    />
                  )}
                  {Array.isArray(formData.optionalFields[field]) && (
                    <button type="button" onClick={() => handleOptionalFieldChange(field, [...formData.optionalFields[field], ''])}>Add {field.replace(/_/g, ' ')}</button>
                  )}
                </div>
              )}
            </div>
          )
        ))}

        <div>
          <input
            type="checkbox"
            checked={formData.includeFields.tool_responses}
            onChange={() => handleIncludeFieldChange('tool_responses')}
            style={{ width: '30px' }}
          /> tool responses
          {formData.includeFields.tool_responses && (
            <div>
              <label>Tool response (business hours)</label>
              <textarea
                value={formData.optionalFields.tool_responses?.tool_response || ''}
                onChange={(e) => handleOptionalFieldChange('tool_responses', {
                  ...formData.optionalFields.tool_responses,
                  tool_response: e.target.value,
                })}
                placeholder={DEFAULT_TOOL_RESPONSES.tool_response}
                rows={2}
                style={{ width: '100%', marginBottom: '8px' }}
              />
              <label>Tool response (after hours)</label>
              <textarea
                value={formData.optionalFields.tool_responses?.tool_response_afterhour || ''}
                onChange={(e) => handleOptionalFieldChange('tool_responses', {
                  ...formData.optionalFields.tool_responses,
                  tool_response_afterhour: e.target.value,
                })}
                placeholder={DEFAULT_TOOL_RESPONSES.tool_response_afterhour}
                rows={3}
                style={{ width: '100%', marginBottom: '8px' }}
              />
              <label>Invalid phone (French)</label>
              <textarea
                value={formData.optionalFields.tool_responses?.invalid_phone_fr || ''}
                onChange={(e) => handleOptionalFieldChange('tool_responses', {
                  ...formData.optionalFields.tool_responses,
                  invalid_phone_fr: e.target.value,
                })}
                placeholder={DEFAULT_TOOL_RESPONSES.invalid_phone_fr}
                rows={2}
                style={{ width: '100%', marginBottom: '8px' }}
              />
              <label>Invalid phone (English)</label>
              <textarea
                value={formData.optionalFields.tool_responses?.invalid_phone_en || ''}
                onChange={(e) => handleOptionalFieldChange('tool_responses', {
                  ...formData.optionalFields.tool_responses,
                  invalid_phone_en: e.target.value,
                })}
                placeholder={DEFAULT_TOOL_RESPONSES.invalid_phone_en}
                rows={2}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>

        {/* TalkLive Section */}
        {selectedAssistant && (
          <div style={{
            border: '1px solid #007bff',
            padding: '16px',
            marginTop: '16px',
            marginBottom: '16px',
            borderRadius: '6px',
            backgroundColor: '#f0f7ff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ fontSize: 15 }}>TalkLive (Missed Calls)</strong>
                <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                  {talkliveInstances.length > 0
                    ? `${talkliveInstances.length} instance${talkliveInstances.length > 1 ? 's' : ''} configured — ${talkliveInstances.map(i => i.label || i.instance_id).join(', ')}`
                    : 'Not configured'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTalkliveModalOpen(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 13
                }}
              >
                {talkliveInstances.length > 0 ? 'Manage TalkLive' : 'Configure TalkLive'}
              </button>
            </div>
          </div>
        )}

        <button type="submit">Set Assistant</button>
      </form>
      {modal.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: 32, borderRadius: 8, minWidth: 300, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            <div style={{ color: modal.isError ? 'red' : 'green', marginBottom: 16 }}>
              {modal.message}
            </div>
            <button onClick={() => {
              setModal({ ...modal, open: false });
              if (!modal.isError && modal.message.includes('Assistant set successfully')) {
                window.location.reload();
              }
            }}>Close</button>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: 32, borderRadius: 8, minWidth: 300, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            <div style={{ marginBottom: 16 }}>
              Are you sure you want to delete this assistant?
            </div>
            <button style={{ marginRight: 16 }} onClick={handleDeleteAssistant}>Confirm</button>
            <button onClick={() => setDeleteModalOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
      {isConverting && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: 32, borderRadius: 8, minWidth: 300, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                border: '2px solid #f3f3f3', 
                borderTop: '2px solid #3498db', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                marginRight: '12px'
              }}></div>
              Converting leads to dealer leads...
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              This may take a few moments. Please wait.
            </div>
          </div>
        </div>
      )}
      <AutoOpenPagesPresetModal
        isOpen={presetModalOpen}
        onClose={() => setPresetModalOpen(false)}
        onApply={handlePresetApply}
        currentValue={formData.optionalFields.auto_open_pages}
      />
      <TalkliveSetupModal
        isOpen={talkliveModalOpen}
        onClose={() => setTalkliveModalOpen(false)}
        assistantId={formData.assistant_id}
        assistantName={formData.name}
        instructionOptions={instructionIdOptions}
        onSave={(updatedInstances) => setTalkliveInstances(updatedInstances)}
      />
    </div>
  );
};

export default DealerFactory;